import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import {
  getClientCategorisationTreatmentQuestionnaire,
  postClientCategorisationTreatmentQuestionnaire,
} from '@/services/api/profile/client-categorisation-treatment-questionnaire';

const profileClientCategorisationTreatmentQueryKey = (pid: UserPid) => ['profile', 'client-categorisation-treatment-questionnaire', pid];

const profileClientCategorisationTreatmentQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getClientCategorisationTreatmentQuestionnaire,
    queryKey: profileClientCategorisationTreatmentQueryKey(pid),
  });

const useProfileClientCategorisationTreatmentQuestionnaire = () => {
  const { pid } = useUserPid();

  return useQuery(profileClientCategorisationTreatmentQueryOptions(pid));
};

const useProfileClientCategorisationTreatment = () => {
  const { data, ...queryResult } = useProfileClientCategorisationTreatmentQuestionnaire();

  return {
    ...queryResult,
    data: data?.answers.treatment ?? null,
  };
};

const useProfileClientCategorisationTreatmentQuestionnaireMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postClientCategorisationTreatmentQuestionnaire,
    onSuccess: async () => {
      await queryClient.resetQueries(profileClientCategorisationTreatmentQueryOptions(pid));
    },
  });
};

export { useProfileClientCategorisationTreatment, useProfileClientCategorisationTreatmentQuestionnaire, useProfileClientCategorisationTreatmentQuestionnaireMutation };
